import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import Modal from "../../../../ui/Modal/Modal";
import { useCallback } from "react";
import {
  ArticleStatus,
  PublishArticlesInput,
} from "../../../../__generated__/gql/graphql";
import PublishModal from "../../../../components/PublishModal/PublishModal";
import { useToast } from "../../../../ui/Toast/ToastContext";

const PublishArticlesModalQuery = graphql(/* GraphQL */ `
  query publishArticlesModal($filters: [ArticleFilter!]) {
    canPublish {
      canPublish
      canPublishToZapier
      canPublishToWordpress
      canPublishToWebflow
    }
    articleCounts(input: { filters: $filters }) {
      published
      total
    }
  }
`);

const PublishArticlesMutation = graphql(/* GraphQL */ `
  mutation publishArticles($input: PublishArticlesInput!) {
    publishArticles(input: $input) {
      count
    }
  }
`);

function PublishArticlesModal({
  isOpen,
  onClose,
  filters,
}: {
  isOpen: boolean;
  onClose: () => void;
  filters: PublishArticlesInput["filters"];
}) {
  const { showToast } = useToast();
  const [canPublishPatternsResponse] = useQuery({
    query: PublishArticlesModalQuery,
    pause: !isOpen,
    variables: { filters },
  });

  const [{ fetching }, publishArticles] = useMutation(PublishArticlesMutation);

  const canPublish = !!canPublishPatternsResponse.data?.canPublish.canPublish;
  const articleCounts = canPublishPatternsResponse.data?.articleCounts;

  const onSubmit = useCallback(
    async (republish: boolean) => {
      const response = await publishArticles({
        input: {
          filters: republish
            ? filters
            : filters?.map((filter) => ({
                ...filter,
                status: filter.status?.map((status) => ({
                  ...status,
                  notIncludes: [
                    ...(status.notIncludes ?? []),
                    ArticleStatus.Published,
                  ],
                })) ?? [{ notIncludes: [ArticleStatus.Published] }],
              })),
        },
      });

      if (response.data?.publishArticles.count) {
        const articles = response.data?.publishArticles.count;

        showToast({
          title: `${articles} ${
            articles === 1 ? "article" : "articles"
          } queued for publishing`,
        });
      }

      onClose();
    },
    [publishArticles, filters, onClose, showToast],
  );

  const title = !canPublish
    ? "Connect your website to publish"
    : `Confirm ${
        articleCounts?.published === articleCounts?.total
          ? "republishing"
          : "publishing"
      } of ${articleCounts?.total} ${
        articleCounts?.total === 1 ? "article" : "articles"
      }`;
  const subtitle =
    articleCounts?.published === articleCounts?.total
      ? "This will replace articles on your site"
      : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      loading={
        canPublishPatternsResponse.fetching || !canPublishPatternsResponse.data
      }
    >
      <PublishModal
        onClose={onClose}
        canPublish={canPublish}
        onSubmit={onSubmit}
        fetching={fetching || !isOpen}
        articleCounts={articleCounts}
      />
    </Modal>
  );
}

export default PublishArticlesModal;
